// Fashion layout (userAccessController.layout === 5).
// Two modes: Browse (style grid + size sheet) and Express (a scan zone, no grid,
// for scan-heavy billing). Phones (≤600px) get the floor-sale screen instead. All cart, order and payment work goes through Core props —
// this layout never writes to the DB. Payment reuses the SuperMarket payment bar
// (FashionPaymentPanel) and the Retail modal set, so behaviour matches layout 4.
//
// Like SuperMarket, an order is processed as soon as the tendered amount covers
// the total (SMPaymentBar → handlePayment). That is deliberate: there is no
// separate "Complete" step on either layout.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import db from "../../../database";
import { buildStyleIndex, findVariant, readVariantAxes } from "../../../utils/fashion/styleIndex";
import { formatMoney } from "../../../utils/fashion/money";
import { colourHex, swatchStyle } from "../../../utils/fashion/colourHex";
import { garmentFor } from "../../../utils/fashion/garmentArt";
import GarmentArt from "./GarmentArt";
import useSyncProgressStore from "../../../stores/useSyncProgressStore";
import RetailModals from "../Retail/RetailModals";
import FashionPaymentPanel from "./FashionPaymentPanel";
import SaleDone, { useSaleDone } from "./SaleDone";
import FashionMenu from "./FashionMenu";
import { configuredLockPin } from "./FashionLockScreen";
import FashionLockGate from "./FashionLockGate";
import FashionBillsPanel, { abandonReturn } from "./FashionBillsPanel";
import FashionCustomerPanel from "./FashionCustomerPanel";
import { customerSummary, isNamedCustomer } from "../../../utils/fashion/customerHistory";
import { useEventBus } from "../../../lib/components/EventBusProvider";
import FashionStockPanel from "./FashionStockPanel";
import { localStockLabel } from "../../../utils/fashion/storeStock";
import { scopeDiscountTypes } from "../../../utils/fashion/discountScope";
import useTransactionStore from "../../../stores/useTransactionStore";
import useCartStore from "../../../stores/useCartStore";
import { exchangeNet, isExchangeBill } from "../../../utils/fashion/returnLines";
import { cancelReturnBill } from "../../../utils/fashion/cancelReturnBill";
import { lineQtyBlockedReason, lineTitle, parseLineQty, piecesLabel } from "../../../utils/fashion/lineQty";
import useWedgeScanner from "./hooks/useWedgeScanner";
import useFashionMarkdowns from "./hooks/useFashionMarkdowns";
import useFashionHolds from "./hooks/useFashionHolds";
import FashionHoldsPanel from "./FashionHoldsPanel";
import FashionAlterationsPanel from "./FashionAlterationsPanel";
import FashionOnlinePanel from "./FashionOnlinePanel";
import FashionCountPanel from "./FashionCountPanel";
import FashionFollowUpsPanel from "./FashionFollowUpsPanel";
import FashionDashboardPanel from "./FashionDashboardPanel";
import FashionOfflinePanel from "./FashionOfflinePanel";
import { NEEDS_CONNECTION, offlineNotice } from "../../../utils/fashion/offlineRules";
import { loadCacheTime } from "../../../utils/fashion/markdowns";
import useOnlineSale from "./hooks/useOnlineSale";
import useReturnPolicyStore from "../../../stores/useReturnPolicyStore";
import { heldQty } from "../../../utils/fashion/holds";
import { FASHION_FEATURES } from "../../../utils/fashion/features";
import { basePrice, dealFor, dealLabel, productForCode, today, withMarkdown } from "../../../utils/fashion/markdowns";
import { billTotals, gstLabel, gstSummaryLabel, taxIncluded } from "../../../utils/fashion/billTotals";
import { resolveFashionTheme } from "../../../utils/fashion/theme";
import useToast from "./till/useToast";
import Toast from "./till/Toast";
import useMediaQuery from "./till/useMediaQuery";
import FashionFloor from "./floor/FashionFloor";
import FashionNavDrawer from "./FashionNavDrawer";
import { tillIdentity } from "../../../utils/fashion/tillIdentity";
import "./fashion-theme.css";
import "./fashion.css";

// A notional price for labelling a cart line's markdown (percentage or fixed).
const LABEL_BASE = 1e9;

const MODE_KEY = "cw.fashion.mode";
const LOCK_KEY = "cw.fashion.locked";
const SEARCH_INPUT_ID = "sm-product-search"; // Core focuses this id after modals close.
const GRID_PAGE = 120;
const EXPRESS_RESULTS = 8;
const PHONE_QUERY = "(max-width: 600px)";
// Below this the header search box is too narrow for the full hint.
const NARROW_BAR_QUERY = "(max-width: 1180px)";
// Portrait tablets: the box is narrower still.
const PORTRAIT_BAR_QUERY = "(max-width: 820px)";
const FOCUSABLE = "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])";

function readMode() {
  try {
    return localStorage.getItem(MODE_KEY) === "express" ? "express" : "browse";
  } catch {
    return "browse";
  }
}

// Swatch shown when a style has no image, or the image fails to load. tint is
// the style's first colour name; the placeholder takes a soft wash of it so a
// grid of missing photos still reads as different products.
const initialsOf = (label) =>
  (label || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

function Swatch({ label, tint }) {
  const initials = initialsOf(label);
  const hex = tint ? colourHex(tint) : null;
  return (
    <div className={`fp-swatch${hex ? " is-tinted" : ""}`} style={hex ? { "--fp-tint": hex } : undefined} aria-hidden="true">
      {initials}
    </div>
  );
}

// Without a photo (or when it fails to load) a style we can place gets a drawn
// garment in its colour; anything else keeps the initials swatch.
function StyleImage({ src, alt, tint, art }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return art ? <GarmentArt art={art} colour={tint} /> : <Swatch label={alt} tint={tint} />;
  return <img className="fp-img" src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

// Size grid columns: up to 4 in a row, choosing 3 or 4 so the last row never
// holds a single size on its own (5 sizes read 3 + 2, not 4 + XXL alone).
export function sizeColumns(n) {
  if (n <= 4) return Math.max(n, 1);
  if (n % 4 === 0) return 4;
  if (n % 3 === 0) return 3;
  return n % 4 === 1 ? 3 : 4;
}

// priceFor(variant) → the markdown deal for that size, or null (see utils/fashion/markdowns).
// onHold(variant), when given, adds a Hold mode: picking a size starts a hold
// instead of adding it to the bill. heldFor(variant) → pieces held in this store.
// showImage=false (no product images on this till) drops the photo column.
export function SizePicker({ style, tillData, onPick, onClose, isOnline, startInStores = false, priceFor, onHold, heldFor, customer, showImage = true }) {
  const [colour, setColour] = useState(style.colours[0] ?? null);
  // "sizes" picks a size for this bill; "stores" shows other stores' stock.
  const [view, setView] = useState(startInStores ? "stores" : "sizes");
  const [holding, setHolding] = useState(false);
  const sizes = style.sizes.length > 0 ? style.sizes : [null];
  const hasStock = sizes.some((s) => localStockLabel(findVariant(style, colour, s)) !== null);
  const dialogRef = useRef(null);
  const addRef = useRef(null);
  // Tapping a size selects it and shows its stock; "Add to bag" adds it. A
  // one-size style starts selected. undefined = nothing picked yet.
  const [picked, setPicked] = useState(() => (sizes.length === 1 ? sizes[0] : undefined));
  const pickedVariant = picked === undefined ? null : findVariant(style, colour, picked);
  const pickedStock = pickedVariant ? localStockLabel(pickedVariant) : null;
  const outHere = pickedStock !== null && pickedStock.startsWith("Out");
  const hereCount = pickedStock && !outHere ? parseInt(pickedStock, 10) : NaN;

  // Start on the first size that is made (or Add to bag when one size is
  // already picked), so a cashier can pick with Enter.
  useEffect(() => {
    const root = dialogRef.current;
    if (!root) return;
    (root.querySelector(".fp-sheet-add") || root.querySelector(".fp-size:not(:disabled)") || root.querySelector("button"))?.focus();
  }, []);
  // After a size is tapped, Enter adds it.
  const pickSize = (s) => {
    setPicked(s);
    setTimeout(() => addRef.current?.focus(), 0);
  };

  // Keep Tab inside the dialog while it is open.
  const trapTab = (e) => {
    if (e.key !== "Tab") return;
    const nodes = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) || [])];
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const priceText = style.minPrice === style.maxPrice ? formatMoney(style.minPrice, tillData) : `${formatMoney(style.minPrice, tillData)}–${formatMoney(style.maxPrice, tillData)}`;

  return (
    <div ref={dialogRef} className={`fp-picker fp-sheet${view === "stores" ? " is-stores" : ""}${showImage ? "" : " no-hero"}`} role="dialog" aria-modal="true" aria-label={`Choose size for ${style.name}`} onKeyDown={trapTab}>
      {showImage && (
        <div className="fp-sheet-hero">
          <StyleImage src={style.imageUrl} alt={style.name} tint={colour ?? style.colours[0]} art={style.art} />
        </div>
      )}
      <div className="fp-sheet-body">
      <div className="fp-picker-head">
        <div>
          <div className="fp-lbl">{style.brandName || "Style"}</div>
          <div className="fp-picker-name">{style.name}</div>
          {style.minPrice != null && <div className="fp-num fp-sheet-price">{priceText}</div>}
        </div>
        <span className="fp-row">
          <button type="button" className="fp-btn-ghost" aria-pressed={view === "stores"} onClick={() => setView((v) => (v === "stores" ? "sizes" : "stores"))}>
            {view === "stores" ? "Back to sizes" : "Other stores"}
          </button>
          {onHold && (
            <button
              type="button"
              className={`fp-btn-ghost${holding ? " is-on" : ""}`}
              aria-pressed={holding}
              onClick={() => {
                setHolding((h) => !h);
                setView("sizes");
              }}
            >
              Hold
            </button>
          )}
          <button type="button" className="fp-btn-ghost" onClick={onClose}>
            Close
          </button>
        </span>
      </div>
      {view === "stores" ? (
        <FashionStockPanel style={style} tillData={tillData} isOnline={isOnline} initialColour={colour} customer={customer} />
      ) : (
        <>
          {style.colours.length > 0 && (
            <div className="fp-field">
              <span className="fp-lbl fp-muted">Colour{colour ? ` · ${colour}` : ""}</span>
              <div className="fp-row fp-swatches">
                {style.colours.map((c) => (
                  <button key={c} type="button" className={`fp-chip fp-sw${c === colour ? " is-on" : ""}`} aria-pressed={c === colour} onClick={() => setColour(c)}>
                    <span className="fp-sw-dot" style={swatchStyle(c)} aria-hidden="true" />
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          <span className="fp-lbl fp-muted">Size</span>
          <div className="fp-sizes" style={{ "--fp-size-cols": sizeColumns(sizes.length) }}>
            {sizes.map((s) => {
              const variant = findVariant(style, colour, s);
              const stock = variant ? localStockLabel(variant) : null;
              const deal = variant ? priceFor?.(variant) : null;
              const price = deal && deal.price < deal.was ? deal.price : variant?.sunitprice;
              const dealText = dealLabel(deal);
              const held = variant ? heldFor?.(variant) || 0 : 0;
              return (
                <button
                  key={s ?? "one"}
                  type="button"
                  className={`fp-size${holding ? " fp-picker-hold" : ""}${!holding && variant && s === picked ? " is-on" : ""}`}
                  disabled={!variant}
                  aria-pressed={holding ? undefined : Boolean(variant) && s === picked}
                  onClick={() => variant && (holding ? onHold(variant) : pickSize(s))}
                  title={variant ? [formatMoney(price, tillData), dealText, stock].filter(Boolean).join(" · ") : "Not made in this colour"}
                >
                  <span className="fp-size-label">{s ?? "One size"}</span>
                  {variant && (deal?.price < deal?.was || style.minPrice !== style.maxPrice) && (
                    <span className="fp-num fp-size-price">
                      {deal && deal.price < deal.was && <s className="fp-was">{formatMoney(deal.was, tillData)}</s>} {formatMoney(price, tillData)}
                    </span>
                  )}
                  {dealText && <span className={`fp-deal${deal.finalSale ? " is-final" : ""}`}>{dealText}</span>}
                  {stock && <span className={`fp-size-stock${stock.startsWith("Out") ? " is-out" : ""}`}>{stock}</span>}
                  {held > 0 && <span className="fp-size-held">{held} held</span>}
                </button>
              );
            })}
          </div>
          {holding ? (
            <p className="fp-muted fp-picker-note" role="status">
              Pick the size to hold for a customer.
            </p>
          ) : !pickedVariant ? (
            <p className="fp-muted fp-picker-note" role="status">
              Pick a size to see stock here and in other stores.
            </p>
          ) : outHere ? (
            <div className="fp-pick-go" role="status">
              <span className="fp-pill p-warn">Not in this store</span>
              <p className="fp-muted fp-picker-note">Hold it at another store or ship it to the customer.</p>
              <span className="fp-row">
                <button type="button" className="fp-btn-primary" onClick={() => setView("stores")}>
                  Other stores
                </button>
                {/* Stock is as of the last sync, so the piece may still be on the rail. */}
                <button ref={addRef} type="button" className="fp-btn-ghost fp-sheet-add" onClick={() => onPick(pickedVariant)}>
                  Add to bag anyway
                </button>
              </span>
            </div>
          ) : (
            <div className="fp-pick-go" role="status">
              {pickedStock && <span className={`fp-pill ${hereCount === 1 ? "p-warn" : "p-ok"}`}>{hereCount === 1 ? "Last one in this store" : `${hereCount} in this store`}</span>}
              <button ref={addRef} type="button" className="fp-btn-primary fp-sheet-add" onClick={() => onPick(pickedVariant)}>
                Add to bag
              </button>
            </div>
          )}
          {!holding && (
            <div className="fp-others">
              {hasStock && <span className="fp-muted">Stock as of the last sync.</span>}
              {/* Ship to home is set on the paid bill (Bills, Ship to home), so this is a pointer, not a button. */}
              <span className="fp-ship-note">
                <b>Ship to home</b> after payment, from the bill in Bills.
              </span>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

// "Threadline Chennai - T Nagar" → ["Threadline Chennai", "T Nagar"]: the header
// shows the brand on one line and the place with the till under it.
export function splitStoreName(name) {
  if (!name) return ["", ""];
  const at = name.indexOf(" - ");
  return at > 0 ? [name.slice(0, at).trim(), name.slice(at + 3).trim()] : [name, ""];
}

function FilterChips({ label, options, value, onChange }) {
  if (options.length < 2) return null;
  return (
    <div className="fp-filter" role="group" aria-label={label}>
      <button type="button" className={`fp-chip${value === "" ? " is-on" : ""}`} onClick={() => onChange("")}>
        All
      </button>
      {options.map((o) => (
        <button key={o.id} type="button" className={`fp-chip${value === o.id ? " is-on" : ""}`} onClick={() => onChange(value === o.id ? "" : o.id)}>
          {o.name}
        </button>
      ))}
    </div>
  );
}

const PointOfSaleFashion = (props) => {
  const {
    cart,
    tillData,
    pickProduct,
    deleteProduct,
    onBarcodeInput,
    parkBill,
    selectProductInCart,
    selectedProductInCart,
    increaseProductQty,
    decreaseProductQty,
    handleCartTotalClick,
    showPaymentMethods,
    setDisplayCustomerSearch,
    setOpenSalesRep,
    setManualDiscountModalVisible,
    manualDiscountModalVisible,
    manualDiscountTypes,
    parkedList,
    retrieveParkedData,
    searchOrderHistory,
    posConfig,
    addProduct,
    setTillLink,
    isOnline,
    displayParkedBillModal,
    setDisplayParkedBillModal,
    displayOrderHistory,
    setDisplayOrderHistory,
    showReturnPurchase,
    setShowReturnPurchase,
    selectedReturnOrder,
  } = props;

  const [mode, setMode] = useState(readMode);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brand, setBrand] = useState("");
  const [openStyle, setOpenStyle] = useState(null);
  // Lock till: "HH:MM" it was locked at, or null. Kept across a reload so a
  // locked till stays locked.
  const [lockedAt, setLockedAt] = useState(() => {
    try {
      return localStorage.getItem(LOCK_KEY) || null;
    } catch {
      return null;
    }
  });
  const [offersDrawerOpen, setOffersDrawerOpen] = useState(false);
  const [gridLimit, setGridLimit] = useState(GRID_PAGE);
  const [qtyDraft, setQtyDraft] = useState("");
  const [qtyInvalid, setQtyInvalid] = useState(false);
  // "parked" | "orders" | "customer" | "holds" | null — shown in place of the style grid.
  const [panel, setPanel] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useCallback(() => setNavOpen(false), []);
  // The size picked in the size picker's Hold mode, waiting for the hold form.
  const [holdDraft, setHoldDraft] = useState(null);
  // One message at a time for the whole till (see till/useToast).
  const { toast, say, clear: clearToast } = useToast();
  const theme = useMemo(() => resolveFashionTheme({ posConfig, tillData }), [posConfig, tillData]);
  const who = useMemo(() => tillIdentity(tillData), [tillData]);
  // Phones get the floor-sale screen; tablets and desktops the till.
  const phone = useMediaQuery(PHONE_QUERY);
  const narrowBar = useMediaQuery(NARROW_BAR_QUERY);
  const portraitBar = useMediaQuery(PORTRAIT_BAR_QUERY);
  // Which discount button opened the drawer: "line" or "bill".
  const [discountScope, setDiscountScope] = useState(null);
  const scanRef = useRef(null);
  const scrimDown = useRef(false);
  const selectBagRef = useRef(null);

  const firstMode = useRef(true);
  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // per-viewer convenience only
    }
    // The scan input moves between the header and the Express scan zone.
    if (firstMode.current) firstMode.current = false;
    else scanRef.current?.focus();
  }, [mode]);

  // Reload the catalogue whenever a product sync finishes (Full Sync clears and
  // refills db.products). toArray + in-memory grouping keeps this safe for both
  // Dexie and SQLite (IPC).
  const productsSyncing = useSyncProgressStore((s) => s.productsTableSyncing);
  useEffect(() => {
    if (productsSyncing) return undefined;
    let alive = true;
    db.products
      .toArray()
      .then((rows) => alive && setProducts(rows || []))
      .catch(() => alive && setProducts([]));
    db.productCategories
      ?.toArray()
      .then((rows) => alive && setCategories(rows || []))
      .catch(() => alive && setCategories([]));
    return () => {
      alive = false;
    };
  }, [productsSyncing]);

  // Each style also carries its drawn garment (shape + fabric from the name,
  // else the category), used wherever there is no photo.
  const styles = useMemo(() => {
    const categoryName = new Map(categories.map((c) => [c.mProductCategoryId, c.name || c.value || ""]));
    return [...buildStyleIndex(products).values()].map((s) => ({ ...s, art: garmentFor(s.name, categoryName.get(s.categoryId)) }));
  }, [products, categories]);
  // Many stores never load product photos. Recognisable garments are drawn
  // instead (data-images="drawn"); with nothing to show at all, tiles become
  // text cards and the bag/scan lists drop their thumbnail column instead of a
  // wall of initials. A style with neither gets a tinted initials placeholder.
  const hasImages = useMemo(() => styles.some((s) => s.imageUrl), [styles]);
  // No photos but recognisable garments: draw them, so the till keeps the
  // picture tiles of the v4 layout instead of falling back to text cards.
  const hasArt = useMemo(() => styles.some((s) => s.art), [styles]);
  const pictures = hasImages || hasArt;
  const hasVariants = useMemo(() => products.some((p) => p?.parentProductId), [products]);

  const categoryOptions = useMemo(() => {
    const used = new Set(styles.map((s) => s.categoryId).filter(Boolean));
    return categories
      .filter((c) => used.has(c.mProductCategoryId))
      .map((c) => ({ id: c.mProductCategoryId, name: c.name || c.value || "" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [styles, categories]);

  const brandOptions = useMemo(
    () =>
      [...new Set(styles.map((s) => s.brandName).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((b) => ({ id: b, name: b })),
    [styles]
  );

  const matchingStyles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return styles
      .filter((s) => (!categoryId || s.categoryId === categoryId) && (!brand || s.brandName === brand))
      .filter((s) => !q || s.name.toLowerCase().includes(q) || s.brandName.toLowerCase().includes(q) || s.variants.some((v) => (v.product.value || "").toLowerCase() === q));
  }, [styles, query, categoryId, brand]);
  const visibleStyles = useMemo(() => matchingStyles.slice(0, gridLimit), [matchingStyles, gridLimit]);

  // A new filter or search starts again from the first page of styles.
  useEffect(() => setGridLimit(GRID_PAGE), [query, categoryId, brand]);

  // Express has no grid; once two characters are typed, offer matching styles
  // so a tag-less item can still be picked by size.
  const expressResults = mode === "express" && query.trim().length >= 2 ? matchingStyles.slice(0, EXPRESS_RESULTS) : [];

  // Colour · size for each cart line, looked up from the synced variant.
  const variantLabels = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      if (!p?.parentProductId) continue;
      const { colour, size } = readVariantAxes(p);
      const label = [colour, size].filter(Boolean).join(" · ");
      if (label) map.set(p.mProductId, label);
    }
    return map;
  }, [products]);

  // A bag line's picture: the variant's own image, else its style's.
  const imageByProduct = useMemo(() => {
    const own = new Map();
    const byStyle = new Map();
    for (const p of products) {
      const img = p?.imageurl || p?.imageUrl;
      if (!img) continue;
      own.set(p.mProductId, img);
      if (p.parentProductId && !byStyle.has(p.parentProductId)) byStyle.set(p.parentProductId, img);
    }
    const map = new Map(own);
    for (const p of products) {
      if (p?.parentProductId && !map.has(p.mProductId) && byStyle.has(p.parentProductId)) map.set(p.mProductId, byStyle.get(p.parentProductId));
    }
    return map;
  }, [products]);

  // A bag line's drawing: its style's garment in the variant's own colour.
  const artByProduct = useMemo(() => {
    const map = new Map();
    for (const s of styles) {
      if (!s.art) continue;
      for (const v of s.variants) {
        if (v.product?.mProductId) map.set(v.product.mProductId, { art: s.art, colour: v.colour || s.colours[0] });
      }
    }
    return map;
  }, [styles]);

  useEffect(() => {
    const onKey = (e) => {
      // Alt+B belongs to Core (parked bills); Alt+M switches the screen mode.
      if (e.key === "F2" && !e.altKey && !e.ctrlKey && !e.metaKey) {
        // F2 jumps to search from anywhere on the till.
        if (!scanRef.current) return;
        e.preventDefault();
        scanRef.current.focus();
        scanRef.current.select?.();
      } else if (e.altKey && (e.key === "m" || e.key === "M" || e.code === "KeyM")) {
        e.preventDefault();
        // A mode switch always lands on that mode's screen, never under an open panel.
        setMode((m) => (m === "browse" ? "express" : "browse"));
        setPanel(null);
        setOpenStyle(null);
      } else if (e.key === "Escape" && openStyle) {
        setOpenStyle(null);
        scanRef.current?.focus();
      } else if (e.key === "Escape" && panel && !document.querySelector(".ant-modal-wrap, .ant-drawer-open")) {
        // Back to styles, unless a Core confirm/drawer on top owns this Escape.
        setPanel(null);
        scanRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openStyle, panel]);

  const closePicker = useCallback(() => {
    setOpenStyle(null);
    scanRef.current?.focus();
  }, []);

  // Season markdowns and final sale for this store (cached for offline use).
  const markdowns = useFashionMarkdowns(tillData, isOnline);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  // When the markdowns used offline were saved, re-read whenever they reload.
  // None while markdowns are switched off, so an old saved time isn't shown.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- markdowns reloading is what rewrites the saved time
  const pricesAt = useMemo(() => (FASHION_FEATURES.markdowns ? loadCacheTime(bunitId) : null), [bunitId, markdowns]);
  const offlineText = offlineNotice(isOnline !== false, posConfig, pricesAt);
  const precision = Number(tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2);
  const priceFor = useCallback(
    (product) => dealFor(markdowns, product?.mProductId, basePrice(product, cart?.customer), today(), bunitId, precision),
    [markdowns, cart?.customer, bunitId, precision]
  );
  // For the cart chip only: which markdown is live for a line (its price is already set).
  const dealByProductId = useCallback((productId) => dealFor(markdowns, productId, LABEL_BASE, today(), bunitId), [markdowns, bunitId]);
  const selectedReturnType = useTransactionStore((s) => s.selectedReturnType);
  // A return bill (not an exchange) takes what is picked as coming back.
  const takingBack = cart?.isReturn === true && selectedReturnType !== "EXCHANGE";

  const handlePick = useCallback(
    (product) => {
      setOpenStyle(null);
      setQuery("");
      scanRef.current?.focus();
      const deal = priceFor(product);
      if (takingBack && deal?.finalSale) {
        say(`${product?.name || "This item"} is final sale and can't be returned.`, "warn");
        return;
      }
      // A return without a receipt refunds the shelf price: today's markdown
      // says nothing about what the customer paid.
      pickProduct?.(takingBack ? product : withMarkdown(product, deal));
    },
    [pickProduct, priceFor, takingBack, say]
  );

  // A tag for a marked-down item (or, on a return, a final-sale one) goes
  // through handlePick so the markdown applies (or the return is refused); every other code goes to Core's scanner.
  const routeScan = (code) => {
    const product = productForCode(products, code);
    const deal = product && product.batchedProduct !== "Y" ? priceFor(product) : null;
    if (deal && ((!takingBack && deal.price < deal.was) || (takingBack && deal.finalSale))) handlePick(product);
    else onBarcodeInput?.(code);
  };

  const handleScan = (e) => {
    e.preventDefault();
    const code = query.trim();
    // While paying the tendered amount is fixed; adding a line would make it stale.
    if (!code || showPaymentMethods) return;
    if (mode === "express" && !panel) {
      expressEnter(code);
      return;
    }
    routeScan(code);
    setQuery("");
  };

  // Express Enter: a tag code goes straight on the bill; a typed style code
  // with one match adds it (one size) or opens its sheet (several sizes).
  const expressEnter = (code) => {
    if (productForCode(products, code)) {
      routeScan(code);
      setQuery("");
      return;
    }
    if (matchingStyles.length === 1) {
      const style = matchingStyles[0];
      if (style.variants.length === 1) handlePick(style.variants[0].product);
      else {
        setOpenStyle(style);
        setQuery("");
      }
      return;
    }
    if (matchingStyles.length > 1) {
      say(`${matchingStyles.length} styles match “${code}”. Pick one below.`, "info");
      return;
    }
    // Core still knows codes this list doesn't (batch and scale barcodes).
    if (/\d/.test(code) && onBarcodeInput) {
      onBarcodeInput(code);
      setQuery("");
      return;
    }
    say(`No style or item matches “${code}”. Check the code on the tag.`, "warn");
  };

  useWedgeScanner(routeScan, { enabled: !showPaymentMethods && !openStyle && !panel && !lockedAt });

  // Sizes held for customers (server-side, shared by the store's tills).
  const holdsApi = useFashionHolds(tillData, isOnline, cart?.sOrderID);
  const heldFor = useCallback((variant) => heldQty(holdsApi.holds, variant?.mProductId), [holdsApi.holds]);
  const productById = useMemo(() => {
    const map = new Map(products.map((p) => [p.mProductId, p]));
    return (id) => map.get(id);
  }, [products]);
  // The store's own price for a product today, without a customer price list:
  // what the online store should also be charging (one price across channels).
  const storePriceFor = useCallback(
    (id) => {
      const p = productById(id);
      if (!p) return null;
      const base = basePrice(p, null);
      return dealFor(markdowns, id, base, today(), bunitId, precision)?.price ?? base;
    },
    [productById, markdowns, bunitId, precision]
  );
  const startHold = useCallback((variant) => {
    setOpenStyle(null);
    setHoldDraft(variant);
    setPanel("holds");
  }, []);
  useEffect(() => {
    if (panel !== "holds") setHoldDraft(null);
  }, [panel]);
  // A held item goes on the bill at its markdown price; once that bill is saved
  // the hold closes as sold (see useFashionHolds). One piece goes on: for a
  // bigger hold the panel stays open with a note to set the quantity.
  const addHoldToBill = (hold, product) => {
    handlePick(product);
    if (hold.status === "HLD") holdsApi.putOnBill(hold);
    if (hold.qty > 1) return `Added 1 of the ${hold.qty} held for ${hold.customerName || hold.mobileNo || hold.documentNo}. Set the quantity on the bill.`;
    setPanel(null);
    return "";
  };

  const items = useMemo(() => (cart?.items || []).filter((item) => item.weight !== 0), [cart?.items]);
  const itemCount = piecesLabel(items);
  const customerName = cart?.customer?.name;
  const showSalesRep = posConfig?.showOrderSalesRep === "Y" && typeof setOpenSalesRep === "function";
  const paying = Boolean(showPaymentMethods);
  const [doneSale, closeDoneSale] = useSaleDone(cart, paying);

  // Retrieving a parked bill drops straight into payment; the panel goes with it.
  useEffect(() => {
    if (paying) setPanel(null);
  }, [paying]);

  // Core's list mixes bill-level (TD) and line-level (FD/PD) rules. Memoised: the
  // drawer re-runs its prefill/reset whenever this array changes identity.
  const scopedDiscountTypes = useMemo(() => scopeDiscountTypes(manualDiscountTypes, discountScope), [manualDiscountTypes, discountScope]);
  useEffect(() => {
    if (!manualDiscountModalVisible) setDiscountScope(null);
  }, [manualDiscountModalVisible]);
  const openDiscount = (scope) => {
    setDiscountScope(scope);
    setManualDiscountModalVisible(true);
  };

  // Same guard Core applies to Alt+B (openDisplayParkedBillModal).
  const handlePark = () => {
    if (useTransactionStore.getState().selectedReturnType === "EXCHANGE") {
      say("Bills can't be parked during a return or exchange.", "warn");
      return;
    }
    clearToast();
    parkBill();
  };

  // Return lines can't be removed one by one, so a return or exchange is
  // dropped as a whole bill, after a second tap.
  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => setConfirmCancel(false), [cart?.documentno, cart?.isReturn, paying]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleCancelBill = () => {
    const done = cancelReturnBill({ deleteCart: props.deleteCart });
    setConfirmCancel(false);
    say(done.ok ? "Return cancelled. Nothing was refunded." : done.message, done.ok ? "ok" : "warn");
  };

  // A message belongs to the bill it was shown for.
  useEffect(() => clearToast(), [cart?.documentno]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePanel = (name) => setPanel((p) => (p === name ? null : name));
  // Browse/Express from the header: close any open panel (Today, Bills...) so the
  // chosen screen shows instead of the panel staying on top of it.
  const switchMode = (next) => {
    setMode(next);
    setPanel(null);
    setOpenStyle(null);
  };
  const backLabel = mode === "express" ? "Back to bill" : "Back to styles";
  // Offline, the screens that need the server stay reachable (each says why it
  // can't load) but look paused, so staff know before they tap.
  const paused = (name) => isOnline === false && NEEDS_CONNECTION.has(name);
  const toolClass = (name) => `fp-tool${panel === name ? " is-on" : ""}${paused(name) ? " is-paused" : ""}`;
  const toolTitle = (name) => (paused(name) ? "Needs a connection" : undefined);

  // Core's layout props carry no dispatchEvent; the return approval rule needs it
  // (SuperMarket and Restaurant read it from the event bus the same way).
  const { dispatchEvent } = useEventBus() || {};

  // History is for a known customer; a walk-in or a cleared customer closes it.
  const namedCustomer = isNamedCustomer(cart?.customer, tillData);
  // "0 pts" says nothing, so a customer with no points shows just the tier (or no chip).
  const customerLine = useMemo(
    () =>
      customerSummary(cart?.customer)
        .split(" · ")
        .filter((part) => part && part !== "0 pts")
        .join(" · "),
    [cart?.customer]
  );
  // The bag shows a customer card for anyone but the till's walk-in; History needs a saved id.
  const customerCard = Boolean(customerName) && (namedCustomer || !cart?.customer?.cwrCustomerId);
  // An online order coming back: no cash refund at the till (see onlineOrders).
  const onlineReturn = useOnlineSale(showReturnPurchase ? selectedReturnOrder : null);
  // Core's return options read this: an online order isn't refunded at the till.
  useEffect(() => {
    useReturnPolicyStore.getState().setRefundBlocked(onlineReturn);
  }, [onlineReturn]);
  useEffect(() => () => useReturnPolicyStore.getState().setRefundBlocked(false), []);
  useEffect(() => {
    if (!namedCustomer) setPanel((p) => (p === "customer" ? null : p));
  }, [namedCustomer]);

  // Core still raises its own parked-bill / order-history modal flags (Alt+B,
  // Alt+O, a cancelled "park and retrieve", the post-sync order refresh). Those
  // modals have no visible form on this layout and could never be closed, so
  // turn each flag back off and show the Fashion panel instead.
  useEffect(() => {
    if (!displayParkedBillModal) return;
    setDisplayParkedBillModal?.(false);
    if (!paying) setPanel("parked");
  }, [displayParkedBillModal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!displayOrderHistory) return;
    setDisplayOrderHistory?.(false);
    if (!paying) setPanel("orders");
  }, [displayOrderHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPayment = () => {
    if (handleCartTotalClick) handleCartTotalClick();
    else props.openPaymentModal?.();
  };

  const showLineSalesRep = showSalesRep && posConfig?.showLineSalesRep === "Y";
  const showOffers = posConfig?.showOffers === "Y";
  const selectedLine = items.find((i) => selectedProductInCart?.lineId && i.lineId === selectedProductInCart.lineId) || null;
  const qtyBlocked = lineQtyBlockedReason(selectedLine, cart);
  const isReturnBill = cart?.isReturn === true;
  // Exchange: `cart` holds the lines coming back (negative), exchangeCart the new
  // ones. Core routes picks, qty changes and payment; this layout only shows both.
  const isExchange = isExchangeBill(selectedReturnType, cart);
  const exchangeCart = useCartStore((s) => s.exchangeCart);
  const exchangeItems = useMemo(() => (isExchange ? (exchangeCart?.items || []).filter((item) => item.weight !== 0) : []), [isExchange, exchangeCart?.items]);
  const billTotal = isExchange ? exchangeNet(cart, exchangeCart) : cart?.total;
  const hasReturnLines = items.some((i) => i.isReturn);
  // Read-only breakdown of the figures Core already worked out.
  const totals = useMemo(() => billTotals(cart), [cart]);
  const gstIncluded = taxIncluded(tillData);

  // A return opened from Orders but not put on the bill is dropped however the
  // panel closes (Escape, the toolbar, payment), so it can't reappear later on
  // top of a sale or leave its approval set for another return.
  const prevPanel = useRef(panel);
  useEffect(() => {
    const left = prevPanel.current === "orders" && panel !== "orders";
    prevPanel.current = panel;
    if (left && !hasReturnLines && (showReturnPurchase || useTransactionStore.getState().salesReturnApproved)) {
      abandonReturn({ setShowReturnPurchase, setCheckedKeys: props.setCheckedKeys });
    }
  }, [panel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once the picked lines are on the bill the return screen has done its job.
  useEffect(() => {
    if (!hasReturnLines || !showReturnPurchase) return;
    setShowReturnPurchase?.(false);
    setPanel((p) => (p === "orders" ? null : p));
  }, [hasReturnLines, showReturnPurchase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the typed quantity whenever another line is selected or its qty changes.
  useEffect(() => {
    setQtyDraft(selectedLine ? String(selectedLine.weight) : "");
    setQtyInvalid(false);
  }, [selectedLine?.lineId, selectedLine?.weight]); // eslint-disable-line react-hooks/exhaustive-deps

  // Absolute quantity, as StockConfirmationDrawer does: addProduct(line, qty, true).
  const commitQty = (e) => {
    e.preventDefault();
    if (!selectedLine || qtyBlocked || typeof addProduct !== "function") return;
    const { qty, error } = parseLineQty(qtyDraft, selectedLine);
    if (error) {
      setQtyInvalid(true);
      say(error, "bad");
      return;
    }
    setQtyInvalid(false);
    if (qty !== Number(selectedLine.weight)) addProduct(selectedLine, qty, true);
  };

  const renderLine = (item) => {
    const variant = variantLabels.get(item.productId);
    const lineDeal = item.isReturn ? null : dealByProductId(item.productId);
    return (
      <li
        key={item.lineId || item.productId}
        className={`fp-line${selectedProductInCart?.lineId && selectedProductInCart.lineId === item.lineId ? " is-sel" : ""}`}
        onClick={() => selectProductInCart?.(item)}
      >
        {pictures && (
          <span className="fp-line-thumb" aria-hidden="true">
            <StyleImage src={item.imageurl || item.imageUrl || imageByProduct.get(item.productId)} alt={item.name} tint={artByProduct.get(item.productId)?.colour} art={artByProduct.get(item.productId)?.art} />
          </span>
        )}
        <span className="fp-line-main">
          <span className="fp-line-name" title={item.name}>
            {lineTitle(item.name, variant)}
          </span>
          {variant && <span className="fp-line-variant">{variant}</span>}
          {lineDeal && <span className={`fp-deal${lineDeal.finalSale ? " is-final" : ""}`}>{dealLabel(lineDeal)}</span>}
          {/* The offer the pricing engine applied to this line (read-only). */}
          {!lineDeal && !item.isReturn && item.discountName && Number(item.discount) > 0 && (
            <span className="fp-pill p-ok fp-line-offer" title={item.discountName}>
              {item.discountName}
            </span>
          )}
        </span>
        <span className="fp-line-col">
          <span className="fp-num fp-line-amt">{formatMoney(item.nettotal, tillData)}</span>
          <span className="fp-line-ctl" onClick={(e) => e.stopPropagation()}>
            <span className="fp-qty">
              {decreaseProductQty && (
                <button type="button" className="fp-qty-btn" aria-label={`One less ${item.name}`} disabled={paying} onClick={() => decreaseProductQty(item)}>
                  −
                </button>
              )}
              <span className="fp-num fp-line-qty">{item.weight}</span>
              {increaseProductQty && (
                <button type="button" className="fp-qty-btn" aria-label={`One more ${item.name}`} disabled={paying} onClick={() => increaseProductQty(item)}>
                  +
                </button>
              )}
            </span>
            <button type="button" className="fp-line-del" aria-label={`Remove ${item.name}`} disabled={paying} onClick={() => deleteProduct?.(item)}>
              ×
            </button>
          </span>
        </span>
      </li>
    );
  };

  // Express without a panel open: the scan input is the big one in the scan zone.
  const expressZone = mode === "express" && !paying && !panel;
  const scanForm = (
    <form className={expressZone ? "fp-bigscan" : "fp-scan"} onSubmit={handleScan} role="search">
      <svg className="fp-scan-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        {expressZone ? <path d="M3 7V4h3M21 7V4h-3M3 17v3h3M21 17v3h-3M7 8v8M10 8v8M13 8v8M17 8v8" /> : (
          <>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </>
        )}
      </svg>
      <input
        id={SEARCH_INPUT_ID}
        ref={scanRef}
        className="fp-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={expressZone ? "Scan the tag, or type a style code and press Enter" : portraitBar ? "Search or scan" : narrowBar ? "Search or scan a tag" : "Search style, item code or scan a barcode"}
        aria-label="Scan or search"
        autoComplete="off"
        autoFocus
      />
      {!expressZone && (
        <button
          type="button"
          className="fp-scan-btn"
          title="Put the cursor in the scan box (F2)"
          onClick={() => {
            scanRef.current?.focus();
            scanRef.current?.select?.();
          }}
        >
          Scan <kbd className="fp-kbd">F2</kbd>
        </button>
      )}
    </form>
  );

  const lockTill = () => {
    const at = new Date();
    const when = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
    setOpenStyle(null);
    setLockedAt(when);
    try {
      localStorage.setItem(LOCK_KEY, when);
    } catch {
      // per-viewer convenience only
    }
  };
  const unlockTill = () => {
    setLockedAt(null);
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      // per-viewer convenience only
    }
    setTimeout(() => scanRef.current?.focus(), 0);
  };
  const lockScreen = lockedAt && (
    <FashionLockGate
      hostProps={props}
      lockKey={LOCK_KEY}
      billOpen={items.length > 0}
      till={who.till}
      user={who.user}
      initials={who.initials}
      lockedAt={lockedAt}
      billText={items.length ? `The open bill (${items.length} ${items.length === 1 ? "item" : "items"}, ${formatMoney(billTotal, tillData)}) is kept.` : "No bill is open."}
      pin={configuredLockPin(posConfig, tillData)}
      onUnlock={unlockTill}
    />
  );

  const csBunit = tillData?.tillAccess?.csBunit?.csBunitId;
  const navItem = (label, name, extra) => ({ label, on: panel === name, paused: paused(name), onClick: () => setPanel(name), ...extra });
  const navGroups = [
    {
      label: "Selling",
      items: [
        { label: "Sell", on: !panel, onClick: () => setPanel(null) },
        retrieveParkedData && navItem("Parked bills", "parked", { badge: parkedList?.length || 0, hint: parkedList?.length ? `${parkedList.length} parked` : undefined }),
        searchOrderHistory && navItem("Orders", "orders"),
        setDisplayCustomerSearch && { label: "Customers", on: panel === "customer", onClick: () => (namedCustomer ? setPanel("customer") : setDisplayCustomerSearch(true)) },
        showOffers && { label: "Offers", onClick: () => setOffersDrawerOpen(true) },
      ].filter(Boolean),
    },
    {
      label: "Floor services",
      items: csBunit
        ? [
            navItem("Alterations", "alterations"),
            navItem("Follow-ups", "followups"),
            holdsApi.enabled && navItem("Holds", "holds"),
            navItem("Online orders", "online"),
            navItem("Stock count", "count"),
          ].filter(Boolean)
        : [],
    },
    {
      label: "Day",
      items: [
        csBunit && navItem("Today", "dashboard"),
        setTillLink && { label: "Sync, close till & sign out", onClick: () => setTillLink(true) },
        { label: "Lock till", onClick: lockTill },
      ].filter(Boolean),
    },
  ];

  // The v4 tile corner tag: a live markdown on the style's first variant.
  const tileTag = (style) => {
    const deal = style.variants?.[0]?.product ? priceFor(style.variants[0].product) : null;
    return deal && !deal.finalSale && deal.price < deal.was ? dealLabel(deal) : "";
  };

  if (phone) {
    return (
      <div className={`fp-floor-host th-${theme}`}>
        <FashionFloor {...props} onPick={handlePick} onScan={routeScan} products={products} categories={categories} themeClass={`th-${theme}`} />
        {lockScreen}
        <Toast toast={toast} onClose={clearToast} />
      </div>
    );
  }

  return (
    <div className={`fp-root th-${theme}`} data-mode={mode} data-paying={paying ? "true" : "false"} data-images={hasImages ? "yes" : hasArt ? "drawn" : "no"}>
      <header className="fp-bar">
        <button
          type="button"
          className="fp-ib fp-more"
          aria-label="Open menu"
          aria-haspopup="dialog"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
          disabled={paying}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <span className="fp-mark" title={[who.store, who.till].filter(Boolean).join(" · ") || undefined}>
          <b>{splitStoreName(who.store)[0] || "Fashion POS"}</b>
          {(splitStoreName(who.store)[1] || who.till) && <small>{[splitStoreName(who.store)[1], who.till].filter(Boolean).join(" · ")}</small>}
        </span>
        <div className="fp-toggle" role="tablist" aria-label="Screen mode" title="Switch with Alt+M">
          <button type="button" role="tab" aria-selected={mode === "browse"} className={mode === "browse" ? "is-on" : ""} onClick={() => switchMode("browse")}>
            Browse
          </button>
          <button type="button" role="tab" aria-selected={mode === "express"} className={mode === "express" ? "is-on" : ""} onClick={() => switchMode("express")}>
            Express
          </button>
        </div>
        {expressZone ? <span className="fp-scan" aria-hidden="true" /> : scanForm}
        <div className="fp-topr">
          {typeof isOnline === "boolean" && (
            <button
              type="button"
              className={`fp-net${isOnline ? "" : " is-off"}${panel === "offline" ? " is-on" : ""}`}
              aria-pressed={panel === "offline"}
              onClick={() => togglePanel("offline")}
              disabled={paying}
              title="What works without a connection"
            >
              <span role="status">{isOnline ? "Online" : "Offline"}</span>
            </button>
          )}
          {who.initials && (
            <span className="fp-avatar" title={`Signed in as ${who.user}`} aria-label={`Signed in as ${who.user}`} role="img">
              {who.initials}
            </span>
          )}
        </div>
      </header>
      <FashionNavDrawer
        open={navOpen && !paying}
        onClose={closeNav}
        title={{ store: who.store || "Fashion POS", sub: [who.till, who.user].filter(Boolean).join(" · ") }}
        groups={navGroups}
        online={typeof isOnline === "boolean" ? { isOnline, text: isOnline ? "Online" : "Offline · what still works", onClick: () => setPanel("offline") } : null}
      />

      {mode === "browse" && !paying && !hasVariants && products.length > 0 && (
        <p className="fp-notice" role="status">
          Sizes and colours aren't on this till yet. Run a Full Sync to load them.
        </p>
      )}
      {offlineText && !paying && (
        <p className="fp-notice fp-notice-offline" role="status">
          {offlineText}
        </p>
      )}
      {isReturnBill && (
        <p className="fp-notice fp-notice-return" role="status">
          {isExchange
            ? "Exchange. Scan or pick the new items; the returned ones are credited against them."
            : hasReturnLines
            ? "Return bill. Check the items coming back, then take the refund."
            : "Return bill. Scan or pick the items being returned, then take the refund."}
        </p>
      )}

      <main className="fp-body">
        {paying ? (
          <FashionPaymentPanel {...props} />
        ) : doneSale && !panel ? (
          <SaleDone order={doneSale} tillData={tillData} onNew={closeDoneSale} />
        ) : panel ? (
          panel === "customer" ? (
            <FashionCustomerPanel customer={cart?.customer} tillData={tillData} isOnline={isOnline} products={products} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "holds" && holdsApi.enabled ? (
            <FashionHoldsPanel
              holdsApi={holdsApi}
              tillData={tillData}
              draftProduct={holdDraft}
              customer={cart?.customer}
              namedCustomer={namedCustomer}
              productById={productById}
              canAddToBill={!isReturnBill}
              onAddToBill={addHoldToBill}
              onDraftDone={() => setHoldDraft(null)}
              backLabel={backLabel}
              onClose={() => setPanel(null)}
            />
          ) : panel === "alterations" ? (
            <FashionAlterationsPanel tillData={tillData} isOnline={isOnline} cart={cart} customer={cart?.customer} namedCustomer={namedCustomer} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "count" ? (
            <FashionCountPanel tillData={tillData} isOnline={isOnline} styles={styles} products={products} productById={productById} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "followups" ? (
            <FashionFollowUpsPanel tillData={tillData} isOnline={isOnline} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "offline" ? (
            <FashionOfflinePanel isOnline={isOnline} posConfig={posConfig} pricesAt={pricesAt} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "dashboard" ? (
            <FashionDashboardPanel tillData={tillData} isOnline={isOnline} posConfig={posConfig} styles={styles} productById={productById} onOpen={setPanel} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : panel === "online" ? (
            <FashionOnlinePanel tillData={tillData} isOnline={isOnline} storePriceFor={storePriceFor} returnProps={{ ...props, dispatchEvent }} onReturnStarted={() => setPanel("orders")} backLabel={backLabel} onClose={() => setPanel(null)} />
          ) : (
            <FashionBillsPanel {...props} dispatchEvent={dispatchEvent} onlineReturn={onlineReturn} panel={panel} backLabel={backLabel} onClose={() => setPanel(null)} />
          )
        ) : mode === "express" ? (
          <section className="fp-express" aria-label="Scan">
            <div className="fp-scanzone">
              {scanForm}
              <span className="fp-muted fp-hint-line">Barcodes carry colour and size, so a scan goes straight on the bill. A typed style code asks for the size first.</span>
              {expressResults.length > 0 && (
                <div className="fp-results" role="group" aria-label="Matching styles">
                  {expressResults.map((s) => (
                    <button key={s.styleId} type="button" className="fp-result" onClick={() => setOpenStyle(s)}>
                      {pictures && (
                        <span className="fp-result-thumb" aria-hidden="true">
                          <StyleImage src={s.imageUrl} alt={s.name} tint={s.colours[0]} art={s.art} />
                        </span>
                      )}
                      <span className="fp-result-main">
                        <span className="fp-result-name">{s.name}</span>
                        {s.code && <span className="fp-result-code fp-muted">{s.code}</span>}
                      </span>
                      <span className="fp-num fp-muted">{s.variants.length > 1 ? `${s.sizes.length || s.variants.length} sizes` : ""}</span>
                      <span className="fp-num">{formatMoney(s.minPrice, tillData)}</span>
                      <span className="fp-result-go">Pick size</span>
                    </button>
                  ))}
                </div>
              )}
              {query.trim().length >= 2 && expressResults.length === 0 && <p className="fp-empty">No style matches. Press Enter to scan it as a tag.</p>}
            </div>
            {/* The bill on the right is the only list of items; this side keeps the scan
                field and, until the first scan, a prompt for it. */}
            {items.length === 0 && (
              <div className="fp-inbag" role="region" aria-label="Start scanning">
                <div className="fp-inbag-empty">
                  <svg className="fp-inbag-art" viewBox="0 0 120 72" aria-hidden="true">
                    <path d="M14 8h-6v12M106 8h6v12M14 64h-6V52M106 64h6V52" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M24 18v36M30 18v36M34 18v36M42 18v36M46 18v36M54 18v36M60 18v36M64 18v36M72 18v36M80 18v36M84 18v36M90 18v36M96 18v36" stroke="currentColor" strokeWidth="2.6" />
                    <path className="fp-inbag-beam" d="M4 36h112" stroke="var(--ac)" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <p className="fp-empty">
                    <strong>Scan the first item.</strong>
                    <span>Each scan goes straight on the bill. Switch to Browse (Alt+M) to find something without a tag.</span>
                  </p>
                </div>
              </div>
            )}
          </section>
        ) : (
          mode === "browse" && (
            <section className="fp-browse" aria-label="Styles">
              {(categoryOptions.length > 1 || brandOptions.length > 1) && (
                <div className="fp-filters">
                  <FilterChips label="Category" options={categoryOptions} value={categoryId} onChange={setCategoryId} />
                  <FilterChips label="Brand" options={brandOptions} value={brand} onChange={setBrand} />
                </div>
              )}
              <div className="fp-grid">
                {visibleStyles.length === 0 && (
                  <p className="fp-empty">{query ? `No styles match “${query}”. Press Enter to scan it as a tag.` : "No styles in this filter."}</p>
                )}
                {visibleStyles.map((s) => (
                  <button key={s.styleId} type="button" className={`fp-card${pictures ? "" : " is-text"}`} onClick={() => setOpenStyle(s)}>
                    {pictures ? (
                      <span className="fp-ph">
                        <StyleImage src={s.imageUrl} alt={s.name} tint={s.colours[0]} art={s.art} />
                        {tileTag(s) && <span className="fp-tag">{tileTag(s)}</span>}
                      </span>
                    ) : (
                      <span className="fp-card-top">
                        <span className="fp-card-brand">{s.brandName || ""}</span>
                        {tileTag(s) && <span className="fp-tag">{tileTag(s)}</span>}
                      </span>
                    )}
                    <span className="fp-card-name">{s.name}</span>
                    <span className="fp-card-meta">
                      <span className="fp-num">
                        {s.minPrice === s.maxPrice ? formatMoney(s.minPrice, tillData) : `${formatMoney(s.minPrice, tillData)}–${formatMoney(s.maxPrice, tillData)}`}
                      </span>
                      {s.sizes.length > 0 && <span className="fp-card-sizes">{s.sizes.length} sizes</span>}
                    </span>
                    {(s.colours.length > 1 || (!pictures && s.colours.length === 1)) && (
                      <span className="fp-card-dots" aria-label={`${s.colours.length} colours`}>
                        {s.colours.slice(0, 5).map((c) => (
                          <span key={c} className="fp-sw-dot" style={swatchStyle(c)} title={c} />
                        ))}
                        {s.colours.length > 5 && <span className="fp-card-more">+{s.colours.length - 5}</span>}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {matchingStyles.length > 0 && (
                <div className="fp-grid-foot">
                  <span className="fp-muted fp-num">
                    Showing {visibleStyles.length} of {matchingStyles.length} styles
                  </span>
                  {visibleStyles.length < matchingStyles.length && (
                    <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setGridLimit((n) => n + GRID_PAGE)}>
                      Show more
                    </button>
                  )}
                </div>
              )}
            </section>
          )
        )}

        <aside className="fp-cart" aria-label="Bill">
          <section className={`fp-bc${customerCard ? " is-named" : ""}`} aria-label="Customer">
            {customerCard ? (
              <>
                <div className="fp-bc-row">
                  <span className="fp-av" aria-hidden="true">
                    {initialsOf(customerName)}
                  </span>
                  <span className="fp-bc-who">
                    <b className="fp-customer-name">{customerName}</b>
                    {cart?.customer?.mobileNo && <span className="fp-muted fp-num">{cart.customer.mobileNo}</span>}
                  </span>
                  <button type="button" className="fp-link" onClick={() => setDisplayCustomerSearch?.(true)} aria-label={`Change customer ${customerName}`}>
                    Change
                  </button>
                </div>
                {namedCustomer && (
                  <div className="fp-bc-row">
                    {customerLine && <span className="fp-bc-tier fp-customer-tier">{customerLine}</span>}
                    <button type="button" className={`fp-link fp-bc-hist${panel === "customer" ? " is-on" : ""}`} aria-pressed={panel === "customer"} onClick={() => togglePanel("customer")} disabled={paying}>
                      History
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="fp-lbl fp-muted">Customer</span>
                <button type="button" className="fp-customer fp-field" onClick={() => setDisplayCustomerSearch?.(true)}>
                  <span className="fp-customer-name">Add customer</span>
                  <span className="fp-muted">Mobile number or name</span>
                </button>
              </>
            )}
          </section>
          <div className="fp-xhead">
            <h2>Bag</h2>
            <span className="fp-muted fp-num">{itemCount}</span>
          </div>
          <ul className="fp-lines">
            {items.length === 0 && <li className="fp-empty">Tap a style, or scan a tag.</li>}
            {items.length > 0 && isExchange && <li className="fp-lines-head fp-lbl fp-muted">Coming back</li>}
            {items.map(renderLine)}
            {isExchange && (
              <>
                <li className="fp-lines-head fp-lbl fp-muted">New items</li>
                {exchangeItems.length === 0 && <li className="fp-empty">Scan or pick what the customer is taking instead.</li>}
                {exchangeItems.map(renderLine)}
              </>
            )}
          </ul>
          {selectedLine && !paying && (
            <div className="fp-line-actions" role="group" aria-label={`Edit ${selectedLine.name}`}>
              {addProduct && (
                <form className="fp-qty-form" onSubmit={commitQty}>
                  <label className="fp-lbl fp-muted" htmlFor="fp-line-qty">
                    Qty
                  </label>
                  <input
                    id="fp-line-qty"
                    className="fp-qty-input fp-num"
                    inputMode="decimal"
                    value={qtyDraft}
                    disabled={Boolean(qtyBlocked)}
                    onChange={(e) => setQtyDraft(e.target.value)}
                    aria-invalid={qtyInvalid ? "true" : "false"}
                    aria-describedby={qtyBlocked ? "fp-line-qty-msg" : undefined}
                  />
                  <button type="submit" className="fp-btn-ghost fp-btn-sm" disabled={Boolean(qtyBlocked)}>
                    Set
                  </button>
                </form>
              )}
              {showLineSalesRep && !selectedLine.isReturn && (
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setOpenSalesRep({ flag: true, level: "line" })}>
                  Line rep
                </button>
              )}
              {setManualDiscountModalVisible && !selectedLine.isReturn && (
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => openDiscount("line")}>
                  Line discount
                </button>
              )}
              {qtyBlocked && (
                <span id="fp-line-qty-msg" className="fp-line-msg">
                  {qtyBlocked}
                </span>
              )}
            </div>
          )}
          {isExchange && (
            <dl className="fp-exch-sum">
              <dt className="fp-muted">Coming back</dt>
              <dd className="fp-num">{formatMoney(cart?.total, tillData)}</dd>
              <dt className="fp-muted">New items</dt>
              <dd className="fp-num">{formatMoney(exchangeCart?.total, tillData)}</dd>
            </dl>
          )}
          {!isExchange && items.length > 0 && (
            <dl className="fp-tot fp-num" aria-label="Bill totals">
              <dt>Subtotal</dt>
              <dd>{formatMoney(totals.items, tillData)}</dd>
              {totals.offerDiscount !== 0 && (
                <>
                  <dt>Offer savings</dt>
                  <dd className="fp-sv">{formatMoney(-totals.offerDiscount, tillData)}</dd>
                </>
              )}
              {totals.billDiscount !== 0 && (
                <>
                  <dt>Bill discount</dt>
                  <dd className="fp-sv">{formatMoney(-totals.billDiscount, tillData)}</dd>
                </>
              )}
              {!gstIncluded && (
                <>
                  <dt className="fp-muted">Taxable value</dt>
                  <dd className="fp-muted">{formatMoney(totals.taxable, tillData)}</dd>
                </>
              )}
              {gstIncluded && totals.gst.length > 0 && (
                <>
                  <dt className="fp-muted">{gstSummaryLabel(totals.gst.map((g) => g.rate), true)}</dt>
                  <dd className="fp-muted">{formatMoney(totals.gst.reduce((n, g) => n + g.amount, 0), tillData)}</dd>
                </>
              )}
              {!gstIncluded &&
                totals.gst.map((g) => (
                  <React.Fragment key={g.rate}>
                    <dt className="fp-muted">{gstLabel(g.rate, false)}</dt>
                    <dd className="fp-muted">{formatMoney(g.amount, tillData)}</dd>
                  </React.Fragment>
                ))}
              {totals.roundOff !== 0 && (
                <>
                  <dt className="fp-muted">Round off</dt>
                  <dd className="fp-muted">{formatMoney(totals.roundOff, tillData)}</dd>
                </>
              )}
            </dl>
          )}
          {/* Total and actions stay pinned together so "To pay" is never hidden under the buttons. */}
          <div className="fp-foot">
            <div className="fp-total">
              <span className="fp-lbl">{(isExchange ? billTotal : cart?.total) < 0 ? "To refund" : "To pay"}</span>
              <span className="fp-num fp-total-amt">{formatMoney(isExchange ? Math.abs(billTotal) : billTotal, tillData)}</span>
            </div>
            {isReturnBill && !paying && confirmCancel && (
              <div className="fp-cancel-confirm" role="group" aria-label="Cancel this return">
                <span>Cancel this {isExchange ? "exchange" : "return"}? Nothing is refunded and the items stay sold.</span>
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setConfirmCancel(false)}>
                  Keep bill
                </button>
                <button type="button" className="fp-btn-ghost fp-btn-sm fp-cancel-yes" onClick={handleCancelBill}>
                  Yes, cancel
                </button>
              </div>
            )}
            {(setManualDiscountModalVisible || showSalesRep || (parkBill && !paying) || (isReturnBill && !paying)) && (
              <div className="fp-acts">
                {setManualDiscountModalVisible && (
                  <button type="button" className="fp-btn-ghost" disabled={items.length === 0 || paying} onClick={() => openDiscount("bill")}>
                    Bill discount
                  </button>
                )}
                {showSalesRep && (
                  <button type="button" className="fp-btn-ghost" onClick={() => setOpenSalesRep({ flag: true, level: "order" })}>
                    Sales rep
                  </button>
                )}
                {isReturnBill && !paying ? (
                  <button type="button" className="fp-btn-ghost fp-cancel-bill" onClick={() => setConfirmCancel(true)} disabled={confirmCancel}>
                    Cancel bill
                  </button>
                ) : (
                  parkBill &&
                  !paying && (
                    <button type="button" className="fp-btn-ghost" onClick={handlePark} disabled={items.length === 0}>
                      Park bill
                    </button>
                  )
                )}
              </div>
            )}
            {!paying && (
              <button type="button" className="fp-btn-pay" onClick={openPayment} disabled={items.length === 0}>
                {isExchange && billTotal < 0 ? "Refund" : "Pay"} {formatMoney(isExchange ? Math.abs(billTotal) : billTotal, tillData)}
              </button>
            )}
          </div>
        </aside>
      </main>

      {openStyle && (
        // Close only on a tap that both starts and ends on the scrim. A tap on a
        // style card opens the sheet under the finger; a touch screen's late
        // (ghost) click must not then close it straight away.
        <div
          className="fp-scrim"
          onPointerDown={(e) => {
            scrimDown.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && scrimDown.current) closePicker();
            scrimDown.current = false;
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <SizePicker style={openStyle} tillData={tillData} isOnline={isOnline} onPick={handlePick} onClose={closePicker} priceFor={priceFor} onHold={holdsApi.enabled && holdsApi.online && tillData?.tillAccess?.csBunit?.csBunitId ? startHold : undefined} heldFor={heldFor} customer={namedCustomer ? cart?.customer : null} showImage={pictures} />
          </div>
        </div>
      )}

      <Toast toast={toast} onClose={clearToast} />
      {lockScreen}
      <FashionMenu {...props} setOffersDrawerOpen={setOffersDrawerOpen} />
      <RetailModals {...props} manualDiscountTypes={scopedDiscountTypes} selectBagRef={selectBagRef} offersDrawerOpen={offersDrawerOpen} setOffersDrawerOpen={setOffersDrawerOpen} />
    </div>
  );
};

export default PointOfSaleFashion;
